<template src="./index.html"></template>
<style src="./style.scss" lang="scss" scoped></style>
<script>
import { mapGetters } from 'vuex';
import validator from 'validator';

import Navbar from '@/components/Navbar';
import ProgressBar from '@/components/ProgressBar';
import InputField from '@/components/InputField';
import NumberSearchButton from '@/components/NumberSearchButton';
import CustomButton from '@/components/CustomButton';
import TeamItem from './components/TeamItem';
import TeamBackgroundCircle from './components/TeamBackgroundCircle';
import OnboardingTeamSkipModal from './components/OnboardingTeamSkipModal';
import CallFlowModal from '@/components/CallFlowModal';
 
import { getAvatarColor } from '@/utils/colors';
import { MUTATION_ADD_TEAMMATE, MUTATION_DELETE_TEAMMATE, MUTATION_DELETE_GROUP, M_ONBOARDING_STEP } from '@/graphql';

export default {
  name: 'InviteTeam',
  components: {
    Navbar,
    ProgressBar,
    InputField,
    NumberSearchButton,
    TeamItem,
    CustomButton,
    TeamBackgroundCircle,
    OnboardingTeamSkipModal,
    CallFlowModal
  },
  data() {
    return {
      isLoading: false,
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      team: [],
      backgroundCircles: [],
      avatarColors: [],
      modalVisible: false,
      callflowModalVisible: false,
      error: '',
      text: '',
      role: 'user'
    };
  },
  computed: {
    ...mapGetters({
      user: 'auth/getUser',
      userGroups: 'auth/getUserGroups',
      existingTeam: 'auth/getUserTeam',
      numberName: 'onboarding/getSelectedName'
    }),
    inviteTeamTitle() {
      return this.team.length > 0 ? 'Add another teammate' : `Let's invite your team`;
    },
    inviteLaterText() {
      return this.team.length > 0 ? '' : 'I’ll invite my team later';
    },
    inviteButtonText() {
      if (this.team.length === 1) {
        return `Invite ${this.team.length} person`;
      }
      return `Invite ${this.team.length} people`;
    },
    addTeamDisabled() {
      return 0;
    }
  },
  mounted() {
    this.team = [...this.existingTeam];
  },
  methods: {
    onAddLater() {
      this.modalVisible = true;
    },

    addBackgroundCircle() {
      const colors = ['#F4F5F7', '#535F77', '#98A0AD'];
      this.backgroundCircles.push({
        left: Math.random() * 100 - 10,
        top: Math.random() * 100 - 10,
        backgroundColor: colors[Math.floor(Math.random() * 3)]
      });
    },

    validateForm() {
      this.error = '';

      // validate the phone number
      if (!validator.isMobilePhone(this.phone, 'en-US')) {
        this.error = 'Whoops, looks like phone number is incorrect';
        this.$refs.phone.setError(this.error);
      }

      // validate the email address
      if (!validator.isEmail(this.email)) {
        this.error = 'Whoops, looks like email address is incorrect';
        this.$refs.email.setError(this.error);
      }

      // check if the email was already added
      const [existing = null] = this.team.length > 0 ? this.team.filter(({ email = '' }) => email === this.email) : [];
      if (existing) {
        this.error = 'A teammate with this email address was already added';
        this.$refs.email.setError(this.error);
      }
    },

    /**
     * Adding a teammate
     * @returns {Promise<string|*>}
     */
    async onAddTeam() {
      try {
        this.validateForm();
        if (this.error) {
          return;
        }

        // store the data on the server
        this.isLoading = true;
        const { data: { insert_users: { returning: result = [] } = {} } = {} } = await this.$apollo.mutate({
          mutation: MUTATION_ADD_TEAMMATE,
          variables: {
            email: this.email,
            firstName: this.firstName,
            lastName: this.lastName,
            phone: this.phone,
            role: this.role
          }
        });

        // check that everything is OK
        const [{ id = null }] = result;
        if (!id) {
          return (this.error = 'Error adding a teammate!');
        }

        // proceed
        this.team.push({
          id,
          avatarColor: getAvatarColor(),
          email: this.email,
          firstName: this.firstName,
          lastName: this.lastName,
          phone: this.phone
        });

        // store team in Vuex
        await this.$store.dispatch('auth/setTeam', { team: [...this.team] });

        this.addBackgroundCircle();

        this.avatarColors.push(getAvatarColor());

        // clear the form
        this.isLoading = false;
        return (this.firstName = this.lastName = this.phone = this.email = '');
      } catch (error) {
        this.isLoading = false;

        // check for the email-related error
        const { message = '' } = error;
        if (message && message.includes('users_email_key')) {
          this.error = 'Email address is already in use!';
          return this.$refs.email.setError(this.error);
        }

        // in any other case
        console.log('-- error @ TEAM INVITATION', error);
        return (this.error = 'Error adding a teammate!');
      }
    },

    /**
     * Delete the added teammate
     * @param {number} userId - ID of the teammate to delete
     * @returns {Promise<boolean|string|*>}
     */
    async onRemoveTeam(userId = null) {
      try {
        // delete the user on the backend
        this.isLoading = true;
        await this.$apollo.mutate({
          mutation: MUTATION_DELETE_TEAMMATE,
          variables: {
            userId
          }
        });

        // update the groups: delete the user from the groups
        const updatedGroups = this.userGroups.map((group) => ({
          ...group,
          members: group.members.filter(({ id }) => id !== userId)
        }));

        // delete empty groups
        const clearedGroups = [];
        for (let i = 0; i < updatedGroups.length; i += 1) {
          if (updatedGroups[i].members.length === 0) {
            if (updatedGroups[i].isCreated) {
              await this.$apollo.mutate({
                mutation: MUTATION_DELETE_GROUP,
                variables: {
                  groupId: updatedGroups[i].id
                }
              });
            }
          } else {
            clearedGroups.push(updatedGroups[i]);
          }
        }

        // update the team: delete the user from the team
        this.team = this.team.filter(({ id }) => id !== userId);
        if (this.team.length === 0) {
          for (let i = 0; i < clearedGroups.length; i += 1) {
            if (clearedGroups[i].isCreated) {
              await this.$apollo.mutate({
                mutation: MUTATION_DELETE_GROUP,
                variables: {
                  groupId: clearedGroups[i].id
                }
              });
            }
          }
          await Promise.all([
            this.$store.dispatch('auth/setGroups', { groups: [] }),
            this.$store.dispatch('auth/setTeam', { team: [] })
          ]);
        } else {
          await Promise.all([
            this.$store.dispatch('auth/setGroups', { groups: [...clearedGroups] }),
            this.$store.dispatch('auth/setTeam', { team: [...this.team] })
          ]);
        }

        await this.$store.dispatch('onboarding/setCallFlow', {});

        return (this.isLoading = false);
      } catch (error) {
        this.isLoading = false;
        console.log('-- error @ TEAM DELETING', error);
        return (this.error = 'Error deleting a teammate!');
      }
    },

    /**
     * Invite button click
     * @returns {void}
     */
    async onInvite() {
      try {
        await this.$apollo.mutate({
          mutation: M_ONBOARDING_STEP,
          variables: {
            user_id: this.user.id,
            onboarding_step: 2
          }
        });
        await this.$store.dispatch('onboarding/setOnboardingStep', 2);
      } catch (error) {
        console.log('-- error @ SET ONBOARDING SETEP', error);
        return (this.error = 'Error set onboarding setep!');
      }

      return this.$router.push('/create-groups');
    },

    onModalBack() {
      this.modalVisible = false;
    },

    onModalNext() {
      this.modalVisible = false;
      this.callflowModalVisible = true;
    },

    async onRoute() {
      await this.$apollo.mutate({
        mutation: M_ONBOARDING_STEP,
        variables: {
          user_id: this.user.id,
          onboarding_step: 3
        }
      });
      await this.$store.dispatch('onboarding/setOnboardingStep', 3);
      this.$router.push('/call-flow');
    }
  }
};
</script>
